import React from 'react';
import { observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from "@material-ui/core/Typography";
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import chroma from 'chroma-js';
import MathJax from 'react-mathjax';
import { athenaBlue } from '../../globals';
import { InfoIcon } from "../../theme/AthenaIcons";
import {
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  XAxis,
  YAxis,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';
import Scenario from '../../api/Scenario';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import DemandSwitches from '../../store/DemandSwitches';
import DemandSwitchesPanel from '../../components/DemandSwitchesPanel';
import DataFrameDownload from '../../components/utils/DataFrameDownload';
import Header from '../../components/utils/Header';
import FilterListView from '../../components/FilterListView';
import DataFrame from "../../components/utils/DataFrame";
import {
  formatFloat,
  formatPercent,
} from '../../utils';
import { PERFORMANCE_FORMULAS } from '../../globals';
// import Version from "../../api/Version";




const styles = theme => ({
  paper: {
    padding: theme.spacing(2),
  },
});


@withStyles(styles)
@withRouter
@observer
class PerformanceMonitoringDemandView extends BaseScenarioDashboardView  {
  static apiSubPath = '/demand_performance_monitoring/' ;

  constructor(props) {
    super(props);
    this.switches = new DemandSwitches({}, this.updateData);
  }

  @observable selectedMonths = null;
  @observable technologyFamily = null;
  @observable anchorInfoTooltip = null;
  @observable geographicalLevel = null;

  _setData(data) {
    this.switches.set('Base demand in scope', data.base_demand_in_scope);
    this.switches.set('Base demand out of scope', data.base_demand_out_scope);
    this.switches.set('Innovation in scope', data.innovation_demand);
    this.switches.set('Risks & opps in scope', data.risks_opps_demand);
    this.selectedMonths = data.selected_month_codes;
    this.technologyFamily = data.technology_family;
    this.geographicalLevel = data.geographical_level;
  }

  _getQueryParams() {
    return {
      base_demand_in_scope: this.switches.get('Base demand in scope'),
      base_demand_out_scope: this.switches.get('Base demand out of scope'),
      innovation_demand: this.switches.get('Innovation in scope'),
      risks_opps_demand: this.switches.get('Risks & opps in scope'),
      selected_month_codes: this.selectedMonths,
      technology_family: this.technologyFamily,
      geographical_level: this.geographicalLevel,
    };
  }

  get initialized() {
    // return super.initialized && Version.fieldsData !== null;
    return super.initialized;
  }

  onSelectedMonthsChange = this.genericChangeHandler('selectedMonths');
  onTechnologyFamilyChange = this.genericChangeHandler('technologyFamily');
  onGeographicalLevelChange = this.genericChangeHandler('geographicalLevel');

  getColor(ratio) {
    return chroma.scale(['#00a8d7', '#cdcaf3'])(ratio).hex();
  };

  get monthSelector() {
    return(
      <Grid item xs>
        {this.renderMultiChoicesField(
          'Displayed months',
          this.data.available_month_codes.map(choice => ({ ...choice, value: parseInt(choice.value) })),
          toJS(this.selectedMonths),
          this.onSelectedMonthsChange,
        )}
      </Grid>
    );
  }

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    const technologyFamilyDict = [
      {"value":"all","display_name":"All"},
      {"value":"powders","display_name":"SN Powders"},
      {"value":"liquids","display_name":"SN Liquids"},
      {"value":"first_diet","display_name":"SN First Diet"}
    ];
    const extraObj = {};
    if (this.selectedMonths){
      extraObj['selected_month_codes'] = this.selectedMonths.slice()
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          {this.renderChoicesField(
            'Geographical level', this.data.geographical_level_choices,
            this.geographicalLevel, this.onGeographicalLevelChange,
          )}
        </Grid>
        <Grid item>
          {this.monthSelector}
        </Grid>
        {/* <Grid item>
          {this.renderChoicesField(
            'Technology family', Version.fieldsData.technology_family.choices, this.technologyFamily,
            this.onTechnologyFamilyChange,
          )}
        </Grid> */}
        <Grid item>
          {this.renderChoicesField(
            'Technology family', technologyFamilyDict, this.technologyFamily,
            this.onTechnologyFamilyChange,
          )}
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/demand_performance_monitoring/?'+ this.getQueryString(extraObj))}
            permission="athena.download_performance_monitoring_demand"
          />
        </Grid>
      </Grid>
    );
  }

  get infoTooltip(){
    const handleClick = event => {
      this.anchorInfoTooltip = event.currentTarget;
    };
    const handleClose = () => {
      this.anchorInfoTooltip = null;
    };
    const open = Boolean(this.anchorInfoTooltip);
    const id = open ? 'simple-popover' : undefined;
  
    const contentInfoToolTip = (
      <MathJax.Provider>
        <ol>
          <li> Athena computes accuracy and bias level using the following fomulas: </li>
          <MathJax.Node formula={PERFORMANCE_FORMULAS.accuracy_tex} />
          <MathJax.Node formula={PERFORMANCE_FORMULAS.bias_tex} />
          <MathJax.Node formula={PERFORMANCE_FORMULAS.m3_bias_tex} />
          <li> Athena uses the following logic to fetch the actuals and forecasts: </li>
          <ul>
            <li> Demand actuals of month "MTH" are extracted from the validated scenario of the current cycle </li>
            <li> Demand forecasts are extracted from </li>
              <ul>
                <li> The validated scenario of month MTH for the "bias" and "accuracy" metrics. Eg. if you are looking at the accuracy of Jan. 2021, forecasts come from the Jan. 2021 scenario </li>
                <li> The validated scenario of month MTH-2 for the "3M-bias" metric. Eg. if you are looking at the accuracy of Jan. 2021, forecasts come from the Nov. 2021 scenario </li>
              </ul>
          </ul>
        </ol>
      </MathJax.Provider>
    );

    return (
      <>
        <Tooltip title="Info">
          <IconButton onClick={handleClick}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
        <Popover
          id={id}
          open={open}
          anchorEl={this.anchorInfoTooltip}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          style={{ 'width': '100%', 'maxHeight': '80%' }}
        >
          <Box m={2}>
            <Typography variant="h6">
              How Athena estimates forecast accuracy and bias
            </Typography>
            <Typography variant="body">
              {contentInfoToolTip}
            </Typography>
          </Box>
        </Popover>
      </>
    );
  };

  get charts() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={400} />;
    }

    const { classes } = this.props;
    const displayedMonths = this.selectedMonths.sort();
    // const geoLevelNames = {
    //   CBU: "Market Name",
    //   Region: "Macro Zone",
    //   Cluster: "CBU Name",
    // }
    // const xAxisDataKey = geoLevelNames[this.geographicalLevel]
    const xAxisDataKey = this.geographicalLevel;
    
    return (
      <Grid item>
        {this.infoTooltip}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Paper className={classes.paper}>
              <Typography variant="h6">
                Forecast accuracy
              </Typography>
              <ResponsiveContainer aspect={21 / 14}>
                <BarChart
                  data={[...this.data.graph_data.demand_accuracy]}
                  margin={{ top: 16, left: 16, right: 16, bottom: 32 }}
                >
                  <XAxis dataKey={xAxisDataKey} interval={0} tick={{fontSize: '80%'}} angle={-35} textAnchor="end"/>
                  <YAxis domain={[0, dataMax => (Math.max(0, dataMax) + 0.01).toFixed(2)]} tickFormatter={formatPercent}/>
                  <RechartsTooltip formatter={(value, name) => ([formatPercent(value, 1), name])} />
                  <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} verticalAlign="top"/>
                  {
                    displayedMonths.map((monthCode, index)=>(
                      this.getBar(monthCode, { name: monthCode, fill: this.getColor((index + 1) / displayedMonths.length) })
                    ))
                  }
                  <ReferenceLine x="Total" strokeDasharray="3 3" stroke={athenaBlue} position="start" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.paper}>
              <Typography variant="h6">
                Forecast Bias
              </Typography>
              <ResponsiveContainer aspect={21 / 14}>
                <BarChart
                  data={[...this.data.graph_data.demand_bias]}
                  margin={{ top: 16, left: 16, right: 16, bottom: 32 }}
                >
                  <XAxis dataKey={xAxisDataKey} interval={0} tick={{fontSize: '80%'}} angle={-35} textAnchor="end"/>
                  <YAxis domain={[
                    dataMin => (Math.min(0, dataMin) - 0.01).toFixed(2),
                    dataMax => (Math.max(0, dataMax) + 0.01).toFixed(2),
                  ]} tickFormatter={formatPercent}/>
                  <RechartsTooltip formatter={(value, name) => ([formatPercent(value, 1), name])} />
                  <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} verticalAlign="top"/>
                  {
                    displayedMonths.map((monthCode, index)=>(
                      this.getBar(monthCode, { name: monthCode, fill: this.getColor((index + 1) / displayedMonths.length) })
                    ))
                  }
                  <ReferenceLine y={0} strokeDasharray="3 3" stroke="#000" alwaysShow />
                  <ReferenceLine x="Total" strokeDasharray="3 3" stroke={athenaBlue} position="start" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.paper}>
              <Typography variant="h6">
                Forecast 3M-Bias
              </Typography>
              <ResponsiveContainer aspect={21 / 14}>
                <BarChart
                  data={[...this.data.graph_data.demand_bias_3]}
                  margin={{ top: 16, left: 16, right: 16, bottom: 32 }}
                >
                  <XAxis dataKey={xAxisDataKey} interval={0} tick={{fontSize: '80%'}} angle={-35} textAnchor="end"/>
                  <YAxis domain={[
                    dataMin => (Math.min(0, dataMin) - 0.01).toFixed(2),
                    dataMax => (Math.max(0, dataMax) + 0.01).toFixed(2),
                  ]} tickFormatter={formatPercent}/>
                  <RechartsTooltip formatter={(value, name) => ([formatPercent(value, 1), name])} />
                  <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} verticalAlign="top"/>
                  {
                    displayedMonths.map((monthCode, index)=>(
                      this.getBar(monthCode, { name: monthCode, fill: this.getColor((index + 1) / displayedMonths.length) })
                    ))
                  }
                  <ReferenceLine y={0} strokeDasharray="3 3" stroke="#000" alwaysShow />
                  <ReferenceLine x="Total" strokeDasharray="3 3" stroke={athenaBlue} position="start" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  get tables() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={400} />;
    }

    const formatTableData = (serializedDataFrame, columnIndex, rowIndex, value) => {
      // TODO: make a more stable logic
      if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
        return '—';
      }
      if (columnIndex >= 2){
        return formatFloat(value * 100, 1);
      }
      return formatFloat(value, 1);
    };


    return (
      <Grid item>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography> {`${this.geographicalLevel} x Supply Table`} </Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.table_geo_supply}
              formatData={formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography> {`${this.geographicalLevel} Table`} </Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.table_geo}
              formatData={formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography> Technology Table </Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.table_techno}
              formatData={formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography> {`${this.geographicalLevel} x Technology Table`} </Typography>
          </AccordionSummary>
          <AccordionDetails overflow='auto'>
            <DataFrame
              serializedDataFrame={this.data.table_geo_techno}
              formatData={formatTableData}
              maxHeight={600}
            />
          </AccordionDetails>
        </Accordion>
        
      </Grid>
    );
  }


  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Demand Performance Monitoring" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        <Grid item>
          <DemandSwitchesPanel switches={this.switches} />
        </Grid>
        <Grid item>
          {this.charts}
        </Grid>
        <Grid item>
          {this.tables}
        </Grid>
      </Grid>
    );
  }
}


export default PerformanceMonitoringDemandView;
